import React, { useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiAlignLeft,
  FiVideo,
  FiCheck,
} from "react-icons/fi";
import { useRoomCalendar } from "../../../../context/RoomCalendarContext";
import { localToISO } from "../../../../utils/calendarUtils";
import "./CalendarCreateModal.css";

// Animation variants
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", damping: 25, stiffness: 300 },
  },
  exit: { opacity: 0, scale: 0.9, y: 20, transition: { duration: 0.2 } },
};

const CalendarCreateModal = ({ roomId, onClose, onSuccess, eventTypes }) => {
  const { createEvent } = useRoomCalendar();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    eventType: "meeting",
    startDate: "",
    endDate: "",
    isAllDay: false,
    location: "",
    meetingLinks: {},
    participants: [],
    recurrence: {
      enabled: false,
      frequency: "weekly",
      interval: 1,
      daysOfWeek: [],
      endDate: null,
      occurrences: null,
    },
    reminders: [{ time: 60, type: "notification" }],
  });

  const [showZoomFields, setShowZoomFields] = useState(false);
  const [showGoogleMeet, setShowGoogleMeet] = useState(false);
  const [showTeams, setShowTeams] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.startDate || !formData.endDate) {
      alert("Please fill in all required fields");
      return;
    }

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (end <= start) {
      alert("End date must be after start date");
      return;
    }

    try {
      setLoading(true);

      const eventData = {
        ...formData,
        startDate: localToISO(formData.startDate),
        endDate: localToISO(formData.endDate),
        researchRoom: roomId,
      };

      const result = await createEvent(roomId, eventData);

      if (result) {
        if (onSuccess) onSuccess(result);
        onClose();
      }
    } catch (error) { /* ignored */ } finally {
      setLoading(false);
    }
  };

  const handleMeetingLinkChange = (platform, field, value) => {
    setFormData((prev) => ({
      ...prev,
      meetingLinks: {
        ...prev.meetingLinks,
        [platform]: {
          ...prev.meetingLinks[platform],
          [field]: value,
        },
      },
    }));
  };

  // Render modal using Portal to avoid parent container clipping
  return ReactDOM.createPortal(
    <motion.div
      className="rcmod-overlay"
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={onClose}
    >
      <motion.div
        className="rcmod-container"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rcmod-header">
          <div className="rcmod-header-left">
            <div className="rcmod-header-icon">
              <FiCalendar />
            </div>
            <div>
              <h2 className="rcmod-title">Schedule Event</h2>
              <p className="rcmod-subtitle">Create a new calendar event</p>
            </div>
          </div>
          <motion.button
            className="rcmod-close-btn"
            onClick={onClose}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FiX />
          </motion.button>
        </div>

        {/* Form */}
        <form className="rcmod-form" onSubmit={handleSubmit}>
          {/* Event Type Selector */}
          <div className="rcmod-type-section">
            <label className="rcmod-label">Event Type</label>
            <div className="rcmod-type-grid">
              {Object.entries(eventTypes || {})
                .filter(([key]) => key !== "all")
                .map(([key, config]) => (
                  <motion.button
                    key={key}
                    type="button"
                    className={`rcmod-type-btn ${
                      formData.eventType === key ? "rcmod-type-btn--active" : ""
                    }`}
                    style={{
                      "--type-color": config.color,
                      "--type-bg": config.bg,
                    }}
                    onClick={() => setFormData({ ...formData, eventType: key })}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span
                      className="rcmod-type-dot"
                      style={{ background: config.color }}
                    />
                    <span className="rcmod-type-label">{config.label}</span>
                    {formData.eventType === key && (
                      <FiCheck className="rcmod-type-check" />
                    )}
                  </motion.button>
                ))}
            </div>
          </div>

          {/* Title */}
          <div className="rcmod-field">
            <label className="rcmod-label" htmlFor="title">
              <FiAlignLeft />
              Event Title *
            </label>
            <input
              id="title"
              type="text"
              className="rcmod-input"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="e.g., Weekly Team Sync"
              required
            />
          </div>

          {/* Description */}
          <div className="rcmod-field">
            <label className="rcmod-label" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              className="rcmod-textarea"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Event details..."
              rows={3}
            />
          </div>

          {/* Date & Time */}
          <div className="rcmod-row">
            <div className="rcmod-field">
              <label className="rcmod-label" htmlFor="startDate">
                <FiClock />
                Start Date & Time *
              </label>
              <input
                id="startDate"
                type="datetime-local"
                className="rcmod-input"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
                required
              />
            </div>

            <div className="rcmod-field">
              <label className="rcmod-label" htmlFor="endDate">
                <FiClock />
                End Date & Time *
              </label>
              <input
                id="endDate"
                type="datetime-local"
                className="rcmod-input"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
                required
              />
            </div>
          </div>

          {/* Location */}
          <div className="rcmod-field">
            <label className="rcmod-label" htmlFor="location">
              <FiMapPin />
              Location
            </label>
            <input
              id="location"
              type="text"
              className="rcmod-input"
              value={formData.location}
              onChange={(e) =>
                setFormData({ ...formData, location: e.target.value })
              }
              placeholder="e.g., Conference Room A"
            />
          </div>

          {/* Meeting Links Section */}
          <div className="rcmod-section">
            <div className="rcmod-section-header">
              <FiVideo />
              <span>Meeting Links</span>
            </div>

            <div className="rcmod-meeting-platforms">
              {/* Zoom */}
              <div className="rcmod-platform">
                <label className="rcmod-platform-toggle">
                  <input
                    type="checkbox"
                    checked={showZoomFields}
                    onChange={(e) => setShowZoomFields(e.target.checked)}
                  />
                  <span className="rcmod-platform-name">Zoom</span>
                </label>

                <AnimatePresence>
                  {showZoomFields && (
                    <motion.div
                      className="rcmod-platform-fields"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <input
                        type="url"
                        className="rcmod-input"
                        placeholder="https://zoom.us/j/..."
                        value={formData.meetingLinks.zoom?.url || ""}
                        onChange={(e) =>
                          handleMeetingLinkChange("zoom", "url", e.target.value)
                        }
                      />
                      <div className="rcmod-platform-row">
                        <input
                          type="text"
                          className="rcmod-input"
                          placeholder="Meeting ID"
                          value={formData.meetingLinks.zoom?.meetingId || ""}
                          onChange={(e) =>
                            handleMeetingLinkChange(
                              "zoom",
                              "meetingId",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="text"
                          className="rcmod-input"
                          placeholder="Password"
                          value={formData.meetingLinks.zoom?.password || ""}
                          onChange={(e) =>
                            handleMeetingLinkChange(
                              "zoom",
                              "password",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Google Meet */}
              <div className="rcmod-platform">
                <label className="rcmod-platform-toggle">
                  <input
                    type="checkbox"
                    checked={showGoogleMeet}
                    onChange={(e) => setShowGoogleMeet(e.target.checked)}
                  />
                  <span className="rcmod-platform-name">Google Meet</span>
                </label>

                <AnimatePresence>
                  {showGoogleMeet && (
                    <motion.div
                      className="rcmod-platform-fields"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <input
                        type="url"
                        className="rcmod-input"
                        placeholder="https://meet.google.com/..."
                        value={formData.meetingLinks.googleMeet?.url || ""}
                        onChange={(e) =>
                          handleMeetingLinkChange(
                            "googleMeet",
                            "url",
                            e.target.value
                          )
                        }
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Teams */}
              <div className="rcmod-platform">
                <label className="rcmod-platform-toggle">
                  <input
                    type="checkbox"
                    checked={showTeams}
                    onChange={(e) => setShowTeams(e.target.checked)}
                  />
                  <span className="rcmod-platform-name">Microsoft Teams</span>
                </label>

                <AnimatePresence>
                  {showTeams && (
                    <motion.div
                      className="rcmod-platform-fields"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <input
                        type="url"
                        className="rcmod-input"
                        placeholder="https://teams.microsoft.com/..."
                        value={formData.meetingLinks.teams?.url || ""}
                        onChange={(e) =>
                          handleMeetingLinkChange(
                            "teams",
                            "url",
                            e.target.value
                          )
                        }
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="rcmod-actions">
            <motion.button
              type="button"
              className="rcmod-cancel-btn"
              onClick={onClose}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </motion.button>
            <motion.button
              type="submit"
              className="rcmod-submit-btn"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <>
                  <span className="rcmod-spinner" />
                  Creating...
                </>
              ) : (
                <>
                  <FiCheck />
                  Create Event
                </>
              )}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default CalendarCreateModal;
